import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "./Cart";
import { PathFinderLoader } from "./components/PathFinderLoader";
import {
  isPrescriptionRequired,
  getPrescriptionButtonText,
  getPrescriptionBadgeClasses,
} from "./utils/prescriptionUtils";
import { getProductImage, isProductHidden } from "./utils/categoryUtils";
import Footer from "./Footer";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const { addToCart } = useCart();

  const defaultImage =
    "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.0.3";

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(
          `https://dawamaxma.dawamtaani.co.ke/api/pharmacy/${id}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch product details");
        }
        const data = await response.json();
        // Check if product belongs to a hidden category
        if (isProductHidden(data)) {
          setError("Product not found");
          setProduct(null);
        } else {
          setProduct(data);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Handle prescription products
  const handleAddToCart = (product) => {
    if (isPrescriptionRequired(product)) {
      setPendingProduct(product);
      setShowPrescriptionModal(true);
    } else {
      addToCart(product);
    }
  };

  const handlePrescriptionUpload = () => {
    if (pendingProduct) {
      addToCart(pendingProduct);
      setShowPrescriptionModal(false);
      setPendingProduct(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50 flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <PathFinderLoader />
          <h2 className="text-2xl font-bold text-neutral-700 mb-2 mt-6">
            Loading Product Details
          </h2>
          <p className="text-neutral-500">
            Please wait while we fetch the product information...
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50 flex items-center justify-center">
        <motion.div
          className="text-center p-8 bg-white rounded-2xl shadow-large"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-neutral-700 mb-2">
            Error Loading Product
          </h2>
          <p className="text-neutral-500 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50 flex items-center justify-center">
        <motion.div
          className="text-center p-8 bg-white rounded-2xl shadow-large"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-neutral-400 text-6xl mb-4">📦</div>
          <h2 className="text-2xl font-bold text-neutral-700 mb-2">
            Product Not Found
          </h2>
          <p className="text-neutral-500 mb-6">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/shop")}
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
          >
            Browse Products
          </button>
        </motion.div>
      </div>
    );
  }

  // Split precautions into bullet points based on sentence endings.
  const precautionsList = product.precautions
    ? product.precautions
        .split(".")
        .filter((sentence) => sentence.trim() !== "")
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50">
      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.0 }}
      >
        {/* Breadcrumb Navigation */}
        <motion.nav
          className="flex items-center space-x-2 text-sm text-neutral-600 mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <button
            onClick={() => navigate("/shop")}
            className="hover:text-primary-600 transition-colors duration-300"
          >
            Shop
          </button>
          <span>›</span>
          <span className="text-neutral-900 font-medium">
            {product.itemName}
          </span>
        </motion.nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative overflow-hidden rounded-2xl bg-white shadow-large group">
              <img
                src={getProductImage(product, defaultImage)}
                alt={product.itemName}
                className="w-full h-96 sm:h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              {/* Subcategory Badge */}
              {product.sub_category && (
                <div className="absolute top-4 left-4">
                  <div className="px-3 py-1 rounded-full text-sm font-medium bg-white/90 text-neutral-800 border border-neutral-200">
                    {product.sub_category}
                  </div>
                </div>
              )}

              {/* Brand Badge */}
              {product.brand && (
                <div className="absolute top-16 left-4">
                  <div className="px-3 py-1 rounded-full text-xs font-medium bg-white/80 text-neutral-700 border border-neutral-200">
                    {product.brand}
                  </div>
                </div>
              )}

              {/* Prescription Badge */}
              <div className="absolute top-4 right-4">
                <div className={getPrescriptionBadgeClasses(product)}>
                  {isPrescriptionRequired(product)
                    ? "Prescription Required"
                    : "Available OTC"}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Product Header */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-large">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-2">
                    {product.itemName}
                  </h1>
                  {product.sub_category && (
                    <div className="mb-2">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700 border border-neutral-200">
                        {product.sub_category}
                      </span>
                    </div>
                  )}
                  {product.brand && (
                    <div className="mb-2">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-neutral-50 text-neutral-700 border border-neutral-200">
                        {product.brand}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <p className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                      Ksh {parseFloat(product.retailPrice).toFixed(2)}
                    </p>
                    {isPrescriptionRequired(product) && (
                      <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                        Rx Required
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  onClick={() => handleAddToCart(product)}
                  className={`flex-1 py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 shadow-medium hover:shadow-large ${
                    isPrescriptionRequired(product)
                      ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700"
                      : "bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="flex items-center justify-center">
                    {getPrescriptionButtonText(product)}
                    <svg
                      className="w-5 h-5 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  </span>
                </motion.button>
                <motion.button
                  onClick={() => navigate("/shop")}
                  className="px-6 py-4 border-2 border-primary-200 text-primary-600 rounded-xl font-semibold hover:bg-primary-50 hover:border-primary-300 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="flex items-center justify-center">
                    Continue Shopping
                    <svg
                      className="w-5 h-5 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </span>
                </motion.button>
              </div>
            </div>

            {/* Description Section */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-large">
              <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                <svg
                  className="w-6 h-6 mr-3 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Description
              </h2>
              <p className="text-neutral-600 leading-relaxed text-lg">
                {product.description ||
                  "No description available for this product."}
              </p>
            </div>

            {/* Precautions Section */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-large">
              <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                <svg
                  className="w-6 h-6 mr-3 text-orange-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                Precautions & Safety Information
              </h2>
              {precautionsList.length > 0 ? (
                <ul className="space-y-3">
                  {precautionsList.map((precaution, index) => (
                    <li key={index} className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-neutral-600 leading-relaxed">
                        {precaution.trim()}.
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-neutral-500 italic">
                  No specific precautions listed for this product.
                </p>
              )}
            </div>

            {/* Additional Information */}
            {isPrescriptionRequired(product) && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-6 sm:p-8">
                <div className="flex items-start">
                  <svg
                    className="w-8 h-8 text-red-500 mr-4 flex-shrink-0 mt-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <div>
                    <h3 className="text-lg font-bold text-red-800 mb-2">
                      Prescription Required
                    </h3>
                    <p className="text-red-700 leading-relaxed">
                      This medication requires a valid prescription from a
                      licensed healthcare provider. Please upload your
                      prescription when adding this item to your cart.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Prescription Upload Modal */}
        {showPrescriptionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full mx-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4">
                Prescription Required
              </h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                This product requires a prescription. Please upload your
                prescription to add it to your cart.
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Upload Prescription
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPrescriptionModal(false)}
                  className="flex-1 px-4 py-3 bg-neutral-100 text-neutral-700 rounded-xl font-semibold hover:bg-neutral-200 transition-colors duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePrescriptionUpload}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
                >
                  Add to Cart
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
      <Footer />
    </div>
  );
};

export default ProductDetail;
